/**
 * Guarda de acceso de las pantallas de Inventario.
 *
 * La ruta ya cuelga de `RequireAuth`; el gating fino lo hace la pantalla (mismo
 * patrón que `RequireAdmin` o `RequirePedidosPermission`). Replica la paridad con
 * el SSR (`docs/frontend_architecture.md`, Pr. 5) SIN inventar navegación:
 *
 *  - **Inventario** (`inventario_controller.inventario`): admin siempre; el resto
 *    necesita `view_inventory` Y que la sede tenga el módulo activo
 *    (`sede_inv_activo`, que ya emite `/auth/me` y gobierna también el menú). Esta
 *    es la MISMA condición que `inject_inv_status`/`navConfig` usan para mostrar la
 *    sección, así que un usuario que ve el enlace puede entrar.
 *  - **Activar por sede** (`inv_admin_page`): solo rol `admin`.
 *
 * Se usa `mode` para no duplicar la tarjeta de "sin acceso"/"cargando".
 */
import type { ReactNode } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { VIEW_PERMISSION } from "../constants";

interface RequireInventarioProps {
  /** "inventario": permiso + sede activa; "admin": solo rol admin. */
  mode: "inventario" | "admin";
  /** Título de la tarjeta (carga / aviso). */
  title: string;
  children: ReactNode;
}

export function RequireInventario({ mode, title, children }: RequireInventarioProps) {
  const { status, hasPermission, sedeInvActivo, user } = useAuth();

  // Mientras la sesión se hidrata (`/auth/me`), aún no se conocen permisos ni el
  // flag de sede: mostrar "sin acceso" en ese instante sería engañoso. Se espera a
  // tener la sesión resuelta (la ruta ya está protegida por `RequireAuth`).
  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500" role="status">
            Cargando…
          </p>
        </CardContent>
      </Card>
    );
  }

  const isAdmin = user?.role === "admin";
  const allowed =
    mode === "admin"
      ? isAdmin
      : // Inventario: admin siempre; el resto, permiso + módulo activo en la sede.
        isAdmin || (hasPermission(VIEW_PERMISSION) && sedeInvActivo);

  if (!allowed) {
    const deniedMessage =
      mode === "admin"
        ? "Solo un administrador puede activar el inventario por sede."
        : "El inventario no está disponible para tu sede o no tienes permiso para verlo.";
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-600" role="alert">
            {deniedMessage}
          </p>
        </CardContent>
      </Card>
    );
  }

  return <>{children}</>;
}
